import React, { useState } from 'react';
import { signup } from '../utils/authService';
import bannerLogo from '../assets/racevoicelogobanner.jpg';

const styles = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'var(--color-bg)',
  },
  card: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 12,
    padding: '40px 36px',
    width: 380,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 20,
  },
  logo: {
    width: 220,
    borderRadius: 8,
    marginBottom: 8,
  },
  title: {
    fontFamily: 'var(--font-heading)',
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--color-secondary)',
    letterSpacing: 3,
    textTransform: 'uppercase',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    outline: 'none',
  },
  button: {
    width: '100%',
    padding: '12px',
    background: 'var(--color-primary)',
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    fontFamily: 'var(--font-heading)',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 2,
    cursor: 'pointer',
    textTransform: 'uppercase',
  },
  loginLink: {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    color: 'var(--color-text-dim)',
  },
  link: {
    color: 'var(--color-accent)',
    cursor: 'pointer',
    textDecoration: 'underline',
    background: 'none',
    border: 'none',
    fontFamily: 'var(--font-body)',
    fontSize: 13,
  },
  error: {
    color: 'var(--color-danger)',
    fontSize: 13,
    fontFamily: 'var(--font-body)',
  },
  success: {
    color: 'var(--color-success)',
    fontSize: 13,
    fontFamily: 'var(--font-body)',
    textAlign: 'center',
    lineHeight: 1.5,
  },
};

export default function SignUpPage({ onNavigateLogin }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    setError('');

    try {
      await signup(email, password, name);
      setSuccess(true);
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={styles.page} data-doc-id="signup-page">
        <div style={styles.card} data-doc-id="signup-success">
          <img src={bannerLogo} alt="RaceVoice" style={styles.logo} />
          <div style={styles.title}>Account Created</div>
          <div style={styles.success}>
            Your account has been created successfully.
            You can now sign in with your credentials.
          </div>
          <button style={styles.button} onClick={onNavigateLogin} data-doc-id="signup-back-to-login">
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page} data-doc-id="signup-page">
      <form style={styles.card} onSubmit={handleSubmit} data-doc-id="signup-form">
        <img src={bannerLogo} alt="RaceVoice" style={styles.logo} />
        <div style={styles.title}>Create Account</div>
        <input
          data-doc-id="signup-name"
          style={styles.input}
          type="text"
          placeholder="Full Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
        />
        <input
          data-doc-id="signup-email"
          style={styles.input}
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
        />
        <input
          data-doc-id="signup-password"
          style={styles.input}
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />
        <input
          data-doc-id="signup-confirm-password"
          style={styles.input}
          type="password"
          placeholder="Confirm Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
        />
        {error && <div style={styles.error}>{error}</div>}
        <button style={{ ...styles.button, opacity: loading ? 0.6 : 1 }} disabled={loading} data-doc-id="signup-submit">
          {loading ? 'Creating Account...' : 'Sign Up'}
        </button>
        <div style={styles.loginLink}>
          Already have an account?{' '}
          <button type="button" style={styles.link} onClick={onNavigateLogin} data-doc-id="signup-go-login">
            Sign In
          </button>
        </div>
      </form>
    </div>
  );
}
